import { tailwindVariants } from '@air/utils-tailwind';

/** One chip line of text: the Something else field grows by this much per wrapped line. */
export const WORKSPACE_AGENT_CHOICE_CHIP_LINE_HEIGHT_PX = 16;

/** Keys 1 to 9 pick options, so only the first nine pills carry a number. */
export const WORKSPACE_AGENT_MAX_NUMBERED_OPTIONS = 9;

/**
 * Every option in a choice dock reads at one size in one set of colors. Picked text never turns blue: it
 * stays gray-12 and steps up a weight; the outline, fill, and mark carry the blue.
 *
 * `option` is top-aligned with a 13px radius (half the one-line height) so a long label wraps into a
 * rounded rect with the same end caps as a one-line pill.
 */
export const workspaceAgentChoiceChip = tailwindVariants({
  base: 'inline-flex max-w-full gap-1.5 border px-2.5 py-1 text-left text-12 leading-4 transition-colors focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9',
  defaultVariants: {
    fullWidth: false,
    selected: false,
    shape: 'pill',
  },
  variants: {
    fullWidth: {
      false: 'w-fit',
      true: 'w-full',
    },
    selected: {
      false:
        'ads:border-default border-gray-4 ads:bg-base bg-gray-1 font-medium ads:text-secondary text-gray-11 ads:hover:border-ads-gray-5 hover:border-gray-5 ads:hover:bg-raised hover:bg-gray-2',
      true: 'ads:border-primary border-altitude-6 ads:bg-primary-subtle bg-altitude-2 font-semibold ads:text-primary text-gray-12',
    },
    shape: {
      option: 'items-start rounded-[13px]',
      pill: 'items-center rounded-full',
    },
  },
});

/** The checkbox on a pick-several chip; a picked pick-one chip fills its number the same way. */
export const workspaceAgentChoiceChipMark = tailwindVariants({
  base: 'flex size-3 shrink-0 items-center justify-center rounded-2 border',
  defaultVariants: {
    checked: false,
  },
  variants: {
    checked: {
      false: 'ads:border-ads-gray-8 border-gray-8 ads:bg-base bg-gray-1',
      true: 'ads:border-primary border-altitude-9 ads:bg-primary bg-altitude-9 text-white',
    },
  },
});

export const workspaceAgentChoiceChipNumber = tailwindVariants({
  base: 'font-bold text-10 uppercase tabular-nums leading-none tracking-[0.4px]',
  defaultVariants: {
    selected: false,
  },
  variants: {
    selected: {
      false: 'flex size-3 shrink-0 items-center justify-center ads:text-accent text-altitude-9',
      true: workspaceAgentChoiceChipMark({ checked: true }),
    },
  },
});

/** Both Something else fields: the chip's box and type, in a text field's rest and focus colors. */
export const workspaceAgentChoiceFreeTextField = tailwindVariants({
  base: 'flex w-full max-w-full items-start gap-1.5 rounded-[13px] border ads:border-input border-gray-5 ads:bg-base bg-gray-1 px-2.5 py-1 ads:focus-within:border-primary focus-within:border-altitude-9',
});

export const workspaceAgentChoiceFreeTextInput = tailwindVariants({
  base: 'min-w-0 flex-1 border-0 bg-transparent p-0 font-medium ads:text-primary text-12 text-gray-12 leading-4 outline-none ads:placeholder:text-placeholder placeholder:text-gray-9',
});
